import React, { useState } from "react";
import { Box, Button, Stack, TextField, Typography, useTheme } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { FormattedMessage } from "react-intl";
import { getAssetHierarchyPalette, getContainedButtonSx, getOutlinedButtonSx, getSelectSx } from "../assetHierarchyStyles";
import { HideIfNoRole } from "../../../auth/authUi";
import { CMMS_MANAGER_ROLES } from "../cmmsRoles";
import cmmsMessages from "../cmmsMessages";
import messages from "../messages";

// AssetNode.Description is nvarchar(500) (AxionOEEContext), and the service does not check the
// length, so anything longer would come back as a 500 rather than a message. The field stops at
// the limit instead.
export const DESCRIPTION_MAX_LENGTH = 500;

/*
 * The description, edited where it is read.
 *
 * Opening the node's edit dialog to change one paragraph means finding the button, finding the
 * field among eight others and saving all of them; here the text turns into a field in place and
 * back. Someone coming from a heavier CMMS should be able to guess how it works.
 *
 * Admin only, like the edit dialog: the PATCH behind it is Cmms_Manager. Everyone else just reads.
 *
 * onSave resolves to whether the save went through. On a failure the field stays open with what
 * was typed, so a rejected save never costs the user their text; the caller has already said why.
 *
 * `disabled` holds off a save while the node is still being saved or re-read, for the reason
 * EditableDetailLine gives.
 *
 * Keyed by node in the caller, so switching node always lands back on the read view.
 */
export default function AssetDescriptionTab({ description, disabled = false, onSave }) {
  const theme = useTheme();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const [draft, setDraft] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const isEditing = draft !== null;
  const current = description ?? "";
  const isUnchanged = isEditing && draft.trim() === current.trim();

  const startEditing = () => setDraft(current);
  const cancelEditing = () => setDraft(null);

  const save = async () => {
    if (isSaving || isUnchanged || disabled) return;
    setIsSaving(true);
    try {
      const saved = await onSave(draft);
      if (saved) setDraft(null);
    } finally {
      setIsSaving(false);
    }
  };

  // The two shortcuts a text box usually has: Ctrl/Cmd+Enter to save, Escape to back out.
  const handleKeyDown = (event) => {
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      save();
    } else if (event.key === "Escape") {
      event.preventDefault();
      cancelEditing();
    }
  };

  if (isEditing) {
    return (
      <Box>
        <TextField
          fullWidth
          multiline
          autoFocus
          minRows={4}
          maxRows={16}
          value={draft}
          disabled={isSaving}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          // Caret at the end, so the obvious first thing — adding to what is there — needs no click.
          onFocus={(event) => event.target.setSelectionRange(event.target.value.length, event.target.value.length)}
          inputProps={{ maxLength: DESCRIPTION_MAX_LENGTH }}
          // getSelectSx is the module's outlined-field treatment; without it the focused border
          // is primary.main, the page navy, and all but disappears.
          InputProps={{ sx: getSelectSx(cmmsColors) }}
          helperText={`${draft.length} / ${DESCRIPTION_MAX_LENGTH}`}
          FormHelperTextProps={{ sx: { color: cmmsColors.mutedText, textAlign: "right", mx: 0 } }}
        />
        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button variant="outlined" onClick={cancelEditing} disabled={isSaving} sx={getOutlinedButtonSx(cmmsColors)}>
            <FormattedMessage {...cmmsMessages.cancel} />
          </Button>
          <Button variant="contained" onClick={save} disabled={isSaving || isUnchanged || disabled} sx={getContainedButtonSx(cmmsColors)}>
            <FormattedMessage {...cmmsMessages.save} />
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Stack direction="row" alignItems="flex-start" sx={{ gap: 2 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {current
          // pre-wrap keeps the line breaks the description was typed with.
          ? <Typography variant="body2" sx={{ color: cmmsColors.text, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {current}
            </Typography>
          : <Typography variant="body2" sx={{ color: cmmsColors.mutedText, fontStyle: "italic" }}>
              <FormattedMessage {...messages.descriptionEmpty} />
            </Typography>
        }
      </Box>

      <HideIfNoRole anyOf={CMMS_MANAGER_ROLES}>
        {current
          ? <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={startEditing} sx={{ ...getOutlinedButtonSx(cmmsColors), flexShrink: 0 }}>
              <FormattedMessage {...cmmsMessages.edit} />
            </Button>
          // An empty description is a gap to fill, so its button is the green one.
          : <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={startEditing} sx={{ ...getContainedButtonSx(cmmsColors), flexShrink: 0 }}>
              <FormattedMessage {...messages.descriptionAdd} />
            </Button>
        }
      </HideIfNoRole>
    </Stack>
  );
}
